import { ColumnDef } from '@tanstack/react-table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { format } from 'date-fns';
import { calculateDateDifference } from '@/utils/compare-dates.ts';
import { cn } from '@/utils/utils.ts';
import { TUser, TUserTotpStatus } from '@/features/users/schemas/users.schema.ts';
import { UsersTableColumnHeader } from './users-table-column-header.tsx';
import { UsersTableRowActions } from './users-table-row-actions.tsx';
import {
  getLabelRole,
  getLabelStatus,
  getTotpStatusBadgeVariant,
  statuses,
} from '@/features/users/schemas/users.utils.ts';

export const columns: ColumnDef<TUser>[] = [
  {
    size: 200,
    accessorKey: 'firstName',
    header: ({ column }) => (
      <UsersTableColumnHeader className="ml-2" column={column} title="Name" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center space-x-2">
          <Avatar className="cursor-pointer">
            <AvatarImage src="" />
            <AvatarFallback className="bg-slate-100">
              {row.original?.firstName?.[0]}
              {row.original?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <span className="max-w-[500px] text-nowrap">
            {row.original?.firstName} {row.original?.lastName}
          </span>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      return `${rowA.original?.firstName} ${rowA.original?.lastName}`.localeCompare(
        `${rowB.original?.firstName} ${rowB.original?.lastName}`
      );
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => {
      return (
        <div className="flex space-x-2">
          <span className="max-w-[500px]">{row.getValue('email')}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = statuses.find((status) => status.value === row.getValue('status'));

      if (!status) {
        return null;
      }

      return (
        <div className="flex w-[100px] items-center text-nowrap">
          <Badge variant={getLabelStatus(status.label)}>{status.label}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'role',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Role" />,
    cell: ({ row }) => {
      const role = row.getValue('role') as string;

      if (!role) {
        return null;
      }

      return (
        <div className="flex items-center">
          <Badge variant={getLabelRole(role)}>{role}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'totpStatus',
    header: ({ column }) => (
      <UsersTableColumnHeader className="text-nowrap" column={column} title="2FA Status" />
    ),
    cell: ({ row }) => {
      const totpStatus = (row.getValue('totpStatus') as TUserTotpStatus) || 'Disabled';

      return (
        <div className="flex items-center">
          <Badge variant={getTotpStatusBadgeVariant(totpStatus)}>{totpStatus}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'lastAccessTokenAt',
    header: ({ column }) => (
      <UsersTableColumnHeader className="text-nowrap" column={column} title="Last Access" />
    ),
    cell: ({ row }) => {
      let label = '';
      let color = '';

      if (row.getValue('lastAccessTokenAt')) {
        const { days: daysDifference } = calculateDateDifference(
          new Date(row.getValue('lastAccessTokenAt')),
          new Date()
        );

        label = format(new Date(row.getValue('lastAccessTokenAt')), 'MMM dd, yyyy HH:mm');
        color = daysDifference < 7 ? 'text-green-500' : 'text-orange-500';
      }

      return (
        <div className="flex space-x-2 text-nowrap">
          <span className={cn('max-w-[500px] font-medium', color)}>{label}</span>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    size: 10,
    id: 'actions',
    cell: ({ row }) => <UsersTableRowActions row={row} />,
    enablePinning: true,
  },
];
